/* ============================================================================
 * ТИПЫ МЕТЕОДАННЫХ ИВО (задача №10)
 *
 * Источник: aviationweather.gov/api/data/metar через адаптер
 * (scripts/weather/metar_adapter.py) и консоль /api/weather.
 *
 * ПРИНЦИП ЧЕСТНОСТИ, заложенный в типы: любое неизвестное значение — null,
 * а НЕ ноль. Ноль для температуры или осадков означал бы «0 °C» и «осадков
 * нет» — это утверждение, которого источник не делал. Поэтому поля, которые
 * METAR может не содержать, объявлены как number | null.
 *
 * Важное различие: stale («данные старше порога») и weather === null
 * («данных нет») — РАЗНЫЕ состояния. Первое — виноват канал связи, второе —
 * источник. Диспетчер должен видеть это различие, поэтому оно не спрятано
 * в один флаг.
 * ========================================================================= */

export interface WeatherCloud {
  /** FEW / SCT / BKN / OVC / VV — количество облачности */
  code: string
  /** Нижняя граница облаков, футы над аэродромом. null если не указана. */
  base_ft?: number | null
}

export interface WeatherSnapshot {
  /** ICAO ближайшей станции, напр. UUEE. null — станция не определена. */
  icao: string | null
  /** Время наблюдения в UTC (из METAR, не время получения). */
  observed_at: string | null
  /** Исходная строка METAR — для сверки диспетчером с первоисточником. */
  metar: string | null

  temp_c: number | null
  dewpoint_c: number | null
  pressure_hpa: number | null

  /** Направление ветра, градусы. null при переменном ветре (VRB). */
  wind_dir_deg: number | null
  /** Скорость ветра, узлы (kt). */
  wind_speed_kt: number | null
  /** true — порыв наблюдался; false — не наблюдался. null — неизвестно. */
  gust_observed: boolean | null

  /** Видимость в метрах. null при видимости «менее 1 км» (METAR пишет M). */
  visibility_m: number | null
  /** Нижняя граница сплошной/низкой облачности, футы. null = нет. */
  ceiling_ft: number | null
  clouds: WeatherCloud[]

  /** CAVOK: видимость ≥10 км, отсутствие значимой облачности и явлений. */
  cavok: boolean | null
  /** Категория полёта: VFR / MVFR / IFR / LIFR. */
  flight_category: string | null

  /** Есть ли достоверные данные об осадках. false = METAR их не содержит. */
  showers_known: boolean | null
  /** Возраст наблюдения в минутах на момент получения. */
  age_min?: number | null
  /** Данные старше порога (METEO_STALE_MIN). */
  stale: boolean
  /** Причины отсутствия/устаревания данных — показываются пользователю. */
  reasons: string[]
  /** Источник данных, для указания происхождения в интерфейсе. */
  source: string | null
  /** Время получения ответа консоли, UTC. */
  fetched_at: string | null
}

/** Категория полёта → состояние палитры статусов ИВО. */
export const FLIGHT_CATEGORY_TONE: Record<
  string,
  'approved' | 'active' | 'warning' | 'critical-error'
> = {
  VFR: 'approved',   // #27B64A — всё в порядке
  MVFR: 'active',    // #1A6FE0 — кооперативный/информационный
  IFR: 'warning',    // #E6B000 — внимание
  LIFR: 'critical-error', // #E03030 — нарушитель
}

/** Подпись категории по-русски — диспетчер читает термины. */
export const FLIGHT_CATEGORY_LABEL: Record<string, string> = {
  VFR: 'ВПР',
  MVFR: 'МВПР',
  IFR: 'ИВПР',
  LIFR: 'ОВПР',
  UNKNOWN: 'нет данных',
}

/**
 * Склонение видимости для подписи. Не «null → прочерк», а явное «менее 1 км»:
 * METAR в этом случае пишет код M, и точное значение неизвестно, но сказать
 * диспетчеру, что видимости достаточно, было бы враньём.
 */
export function formatVisibility(m: number | null | undefined): string {
  if (m === null || m === undefined) return '—'
  if (m >= 10000) return '10 км'
  if (m >= 1000) return `${(m / 1000).toFixed(1).replace('.', ',')} км`
  return `${m} м`
}

/** Облачность строкой: «BKN 500 фут, OVC 1800 фут» или «CAVOK». */
export function formatClouds(
  clouds: WeatherCloud[] | null | undefined,
  cavok: boolean | null | undefined,
): string {
  if (cavok) return 'CAVOK'
  if (!clouds || clouds.length === 0) return '—'
  return clouds
    .map((c) => `${c.code}${c.base_ft ? ` ${c.base_ft} фут` : ''}`)
    .join(', ')
}

/**
 * Направление и скорость ветра одной строкой.
 * Переменный ветер (wind_dir_deg === null при скорости > 0) подписывается
 * явно: в METAR это VRB, и показывать «0°» значило бы выдумать курс.
 */
export function formatWind(
  dir: number | null | undefined,
  speed_kt: number | null | undefined,
  gust: boolean | null | undefined,
): string {
  if (speed_kt === null || speed_kt === undefined) return '—'
  const speed = `${speed_kt.toFixed(1).replace('.', ',')} уз`
  if (dir === null || dir === undefined) return `VRB ${speed}`
  return `${String(dir).padStart(3, '0')}° ${speed}${gust ? ' (порыв)' : ''}`
}